export function swiperInit() {
	/**
	 * --- HELPER UTILITIES ---
	 */
	const getSlidesPerGroup = (slidesSizesGrid, visibleSlidesIndexes) => {
		const totalSlide = slidesSizesGrid.length;
		const visibleSlide = visibleSlidesIndexes?.length ?? 1;
		return totalSlide <= visibleSlide ? null : visibleSlide;
	};

	const triggerLozad = (swiper) => {
		const nextSlide = swiper.slides[swiper.activeIndex + 1];
		if (!nextSlide) return;

		const nextImages = nextSlide.querySelectorAll("img");
		// Check if lazyLoader (Lozad) exists globally
		if (window.lazyLoader && typeof window.lazyLoader.triggerLoad === "function") {
			nextImages.forEach((img) => window.lazyLoader.triggerLoad(img));
		}
	};

	const autoSetSlidesPerGroup = (swiper) => {
		if (!swiper.pagination?.el) return;
		const { visibleSlidesIndexes, slidesSizesGrid } = swiper;
		const slidesPerGroup = getSlidesPerGroup(slidesSizesGrid, visibleSlidesIndexes);

		if (slidesPerGroup !== null && slidesPerGroup > 0 && slidesPerGroup !== swiper.params.slidesPerGroup) {
			swiper.params.slidesPerGroup = slidesPerGroup;
			swiper.update();
		}
	};

	/**
	 * --- MODULE 1: PRIMARY BANNER (Video & Progress logic) ---
	 */
	const initPrimaryBanner = () => {
		const bannerEl = document.querySelector(".primary-banner .swiper");
		if (!bannerEl) return;

		let progressInterval = null;
		let timer = null;
		let isPaused = false;
		const slideTimeout = 40000;

		const primarySwiper = new Swiper(bannerEl, {
			slidesPerView: 1,
			observer: true,
			observeParents: true,
			speed: 1205,
			effect: "fade",
			fadeEffect: { crossFade: true },
			loop: true,
			init: false,
			navigation: {
				nextEl: ".primary-banner .next",
				prevEl: ".primary-banner .prev",
			},
			pagination: {
				el: ".primary-banner .swiper-pagination",
				clickable: true,
				renderBullet: (index, className) => `<span class="${className}" data-index="${index}"></span>`,
			},
		});

		const resetProgress = () => {
			if (progressInterval) clearInterval(progressInterval);
			const track = document.querySelector(".primary-banner .progress-track");
			if (track) track.style.width = "0%";
		};

		const startProgress = () => {
			resetProgress();
			if (isPaused) return;
			const track = document.querySelector(".primary-banner .progress-track");
			if (!track) return;

			let progress = 0;
			progressInterval = setInterval(() => {
				if (!isPaused) {
					progress += (20 / slideTimeout) * 100;
					track.style.width = Math.min(progress, 100) + "%";
					if (progress >= 100) clearInterval(progressInterval);
				}
			}, 20);
		};

		const handleMedia = (slide) => {
			if (timer) clearTimeout(timer);
			if (!isPaused) resetProgress();

			document.querySelectorAll(".primary-banner-video").forEach(v => v.pause());

			const isMobile = window.innerWidth <= 768;
			const video = slide.querySelector(isMobile ? "video.mobile" : "video.desktop") || slide.querySelector("video");

			if (video) {
				if (!isPaused) {
					startProgress();
					video.play().catch(() => {
						timer = setTimeout(() => primarySwiper.slideNext(), slideTimeout);
					});
				}
				video.onended = () => {
					if (!isPaused) primarySwiper.slides.length === 1 ? (video.currentTime = 0, video.play()) : primarySwiper.slideNext();
				};
			} else if (!isPaused) {
				startProgress();
				timer = setTimeout(() => primarySwiper.slideNext(), slideTimeout);
			}
		};

		// Events
		primarySwiper.on("slideChangeTransitionEnd", () => handleMedia(primarySwiper.slides[primarySwiper.activeIndex]));
		primarySwiper.on("slideChangeTransitionStart", () => !isPaused && resetProgress());
		primarySwiper.on("init", () => handleMedia(primarySwiper.slides[primarySwiper.activeIndex]));

		// Controls
		document.querySelector(".pause-track")?.addEventListener("click", function () {
			isPaused = !isPaused;
			this.classList.toggle("paused", isPaused);
			const video = primarySwiper.slides[primarySwiper.activeIndex].querySelector("video");

			if (isPaused) {
				if (progressInterval) clearInterval(progressInterval);
				if (timer) clearTimeout(timer);
				video?.pause();
			} else {
				video ? video.play() : (timer = setTimeout(() => primarySwiper.slideNext(), slideTimeout));
				startProgress();
			}
		});

		primarySwiper.init();
	};

	/**
	 * --- MODULE 2: GENERIC AUTO-INIT SLIDERS ---
	 */
	const initGenericSliders = () => {
		window.initSwiper = window.initSwiper || [];
		const sliderNodes = document.querySelectorAll(".init-swiper .swiper");

		sliderNodes.forEach((el, index) => {
			const parent = el.closest(".init-swiper");
			if (!parent) return;

			const time = parseInt(el.getAttribute("data-time")) || 5000;
			const isTriggerLoadedNext = el.classList.contains("trigger-loaded-next");

			// Unique Classes
			el.classList.add(`swiper-init-${index}`);
			parent.querySelector(".prev")?.classList.add(`prev-nav-${index}`);
			parent.querySelector(".next")?.classList.add(`next-nav-${index}`);
			parent.querySelector(".swiper-pagination")?.classList.add(`pagination-inst-${index}`);

			let pType = "bullets";
			if (el.classList.contains("fraction")) pType = "fraction";
			if (el.classList.contains("progress")) pType = "progressbar";

			const options = {
				observer: true,
				observeParents: true,
				rewind: true,
				speed: 1205,
				watchSlidesProgress: true,
				loop: false,
				autoplay: time > 0 ? { delay: time, pauseOnMouseEnter: true } : false,
				slidesPerView: "auto",
				centeredSlides: el.classList.contains("is-center-slide"),
				initialSlide: parseInt(el.getAttribute("data-initial")) || 0,
				navigation: {
					nextEl: `.next-nav-${index}`,
					prevEl: `.prev-nav-${index}`,
				},
				pagination: {
					el: `.pagination-inst-${index}`,
					type: pType,
					clickable: true,
				},
				on: {
					beforeInit: function () { window.initSwiper.push(this); },
					init: function () {
						if (isTriggerLoadedNext) triggerLozad(this);
					},
					slideChange: function () {
						if (el.classList.contains("slide-has-video")) {
							el.querySelectorAll("video").forEach(v => v.pause());
							el.querySelectorAll(".swiper-slide").forEach(s => s.classList.remove("play-video"));
						}
						if (isTriggerLoadedNext) triggerLozad(this);
					},
					resize: function () {
						const totalWidth = this.slidesSizesGrid.reduce((a, b) => a + b, 0);
						this.el.classList.toggle("center-slide-active", totalWidth < this.size);
						autoSetSlidesPerGroup(this);
					}
				}
			};

			// Infinite scroll override
			if (time === 0) {
				options.autoplay = { delay: 0, disableOnInteraction: false };
				options.loop = true;
				options.speed = 1800;
				delete options.navigation;
			}

			new Swiper(`.swiper-init-${index}`, options);
		});
	};

	/**
	 * --- MODULE 3: SYNCED THUMB SLIDERS ---
	 */
	const initThumbSliders = () => {
		const thumbEl = document.querySelector(".product-thumb .swiper");
		const mainEl = document.querySelector(".product-main .swiper");
		if (!thumbEl || !mainEl) return;

		const homeThumb = new Swiper(thumbEl, {
			speed: 1205,
			spaceBetween: 20,
			breakpoints: {
				200: { slidesPerView: 3 },
				768: { slidesPerView: 4 },
				1024: { slidesPerView: 5 },
				1280: { slidesPerView: 4 },
				1400: { slidesPerView: 6 },
			},
		});

		const homeMain = new Swiper(mainEl, {
			speed: 1205,
			effect: "fade",
			fadeEffect: { crossFade: true },
			autoplay: { delay: 3000 },
			thumbs: { swiper: homeThumb },
			navigation: {
				nextEl: ".product-detail .next",
				prevEl: ".product-detail .prev",
			},
		});

		// Hover Sync logic
		const thumbSlides = thumbEl.querySelectorAll(".swiper-slide");
		thumbSlides.forEach((slide, index) => {
			slide.addEventListener("mouseenter", () => {
				homeMain.autoplay.stop();
				homeMain.slideTo(index);
			});
			slide.addEventListener("mouseleave", () => {
				homeMain.autoplay.start();
			});
		});

		homeMain.on("slideChange", () => homeThumb.slideTo(homeMain.activeIndex));

		window.homeThumb = homeThumb;
		window.homeMain = homeMain;
	};

	/**
	 * --- EXECUTION ---
	 */

	try {
		initPrimaryBanner();
		initGenericSliders();
		initThumbSliders();

		// Misc Video Clicks
		document.querySelectorAll(".primary-banner-video").forEach(video => {
			video.addEventListener("click", function () {
				if (this.paused) {
					this.play();
					this.parentElement.classList.remove("pause");
				} else {
					this.pause();
					this.parentElement.classList.add("pause");
				}
			});
		});
	} catch (error) {
		console.error("SwiperInit Error:", error);
	}
}